import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  RefreshCcw,
  ExternalLink,
  Loader2,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  ChevronDown,
  Download,
  Check,
  Users,
  Repeat2,
  Bookmark,
  PlayCircle,
  Sparkles,
  MousePointerClick,
  UserPlus,
  UserCheck,
  Timer,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Images,
  X,
  Info,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from '../components/AccountAvatar';
import { BestTimesCard } from '../components/BestTimesCard';

/* ── Types ──────────────────────────────────────────────── */
type Account = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
  status: 'connected' | 'disconnected';
  /** LinkedIn: 'page' for Company Pages, null for personal profiles. */
  connectionType?: string | null;
};

type PostKind = 'post' | 'reel' | 'story';

type AnalyticsData = {
  totals: {
    likes: number;
    comments: number;
    shares: number;
    views: number;
    reach: number;
    engagement: number;
    linkClicks: number;
    profileVisits: number;
    followsGained: number;
    watchTimeSeconds: number;
    postCount: number;
  };
  kindCounts: { post: number; reel: number; story: number };
  // Set when postCount is 0 but the user/account does have metrics outside
  // the requested window — lets the UI nudge the user to widen the period
  // instead of showing a generic "no data" wall.
  outsideRange: { latestPostedAt: string; totalCount: number } | null;
  // Present only when the request was made with ?compare=previous — gives
  // the same totals shape for the preceding equal-length window so the UI
  // can render percentage deltas next to each metric.
  compare?: { totals: AnalyticsData['totals']; from: string; to: string };
  series: Array<{
    date: string;
    likes: number;
    comments: number;
    shares: number;
    views: number;
    reach: number;
  }>;
  topPosts: Array<{
    id: string;
    platform: string;
    kind: PostKind;
    platformUrl: string | null;
    caption: string | null;
    mediaUrl: string | null;
    mediaUrls: string[];
    accountName: string | null;
    postedAt: string | null;
    likes: number;
    comments: number;
    shares: number;
    views: number;
    reach: number;
    linkClicks: number;
    profileVisits: number;
    followsGained: number;
    watchTimeSeconds: number;
    engagement: number;
  }>;
  postCount: number;
  // True when the server is refreshing metrics in the background (cache was
  // stale). The UI shows cached data instantly and auto-refetches shortly after.
  refreshing?: boolean;
};

/* ── Helpers ────────────────────────────────────────────── */
const fmt = new Intl.NumberFormat('es-ES');

function fmtNum(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 10_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return fmt.format(n);
}

function fmtFull(n: number): string {
  return fmt.format(n);
}

/** Seconds → "3 h 12 min", "45 min", "38 s" (watch time cards). */
function fmtDuration(totalSeconds: number): string {
  const s = Math.round(totalSeconds);
  if (s < 60) return `${s} s`;
  const minutes = Math.round(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours >= 100) return `${fmt.format(hours)} h`;
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function fmtDateShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

function fmtDateLong(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function truncate(text: string, max = 80): string {
  return text.length > max ? text.slice(0, max).trimEnd() + '…' : text;
}

function isPlatform(p: string): p is Platform {
  return p in PLATFORM_META;
}

const DAY_OPTIONS = [30, 60, 90] as const;
type DayOption = (typeof DAY_OPTIONS)[number];
const MAX_CUSTOM_DAYS = 90;

/** Local YYYY-MM-DD (avoids the UTC shift `Date.toISOString()` introduces in es-ES timezones). */
function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type MetricKey = 'likes' | 'comments' | 'shares' | 'views' | 'reach';
const METRIC_DEFAULTS: Record<MetricKey, { label: string; color: string }> = {
  likes: { label: 'Me gusta', color: '#f43f5e' },
  comments: { label: 'Comentarios', color: '#f59e0b' },
  shares: { label: 'Compartidos', color: '#10b981' },
  views: { label: 'Vistas', color: '#3b82f6' },
  reach: { label: 'Alcance', color: '#8b5cf6' },
};

/**
 * Returns the chart metric configs (key + label + color) for the given
 * platform. Mirrors getStatCards: when a single account is selected we
 * use the platform-specific wording and hide metrics that platform doesn't
 * expose. Backend series always carries all 5 numeric fields.
 */
function getChartMetrics(
  platform: Platform | null,
  kind: PostKind | null = null,
): Array<{ key: MetricKey; label: string; color: string }> {
  const make = (key: MetricKey, label?: string) => ({
    key,
    label: label ?? METRIC_DEFAULTS[key].label,
    color: METRIC_DEFAULTS[key].color,
  });
  // Instagram-specific kind overrides — Stories don't have "Vistas" or "Shares",
  // Reels lean heavily on Reproducciones/Views.
  if (platform === 'instagram' && kind === 'story') {
    return [make('reach'), make('likes'), make('comments', 'Respuestas')];
  }
  if (platform === 'instagram' && kind === 'reel') {
    return [make('views', 'Reproducciones'), make('reach'), make('likes'), make('comments'), make('shares')];
  }
  switch (platform) {
    case 'instagram':
      return [make('reach'), make('views'), make('likes'), make('comments'), make('shares')];
    case 'facebook':
      return [make('reach'), make('views', 'Reproducciones'), make('likes'), make('comments'), make('shares')];
    case 'twitter':
      return [make('views', 'Impresiones'), make('likes'), make('comments', 'Respuestas'), make('shares', 'Reposts')];
    case 'tiktok':
    case 'tiktok_business':
      return [make('views', 'Reproducciones'), make('likes'), make('comments'), make('shares')];
    case 'threads':
      return [make('views'), make('likes'), make('comments', 'Respuestas'), make('shares', 'Reposts')];
    case 'linkedin':
      return [make('views', 'Impresiones'), make('likes', 'Reacciones'), make('comments'), make('shares')];
    case 'bluesky':
      // Bluesky API does not expose views/impressions
      return [make('likes'), make('comments', 'Respuestas'), make('shares', 'Reposts')];
    case 'pinterest':
      return [make('views', 'Impresiones'), make('likes', 'Reacciones'), make('comments'), make('shares', 'Guardados')];
    case 'youtube':
      return [make('views', 'Reproducciones'), make('likes'), make('comments'), make('shares')];
    default:
      return [make('views'), make('likes'), make('comments'), make('shares')];
  }
}

/** Possible stat-card field on the totals object. */
type StatField =
  | 'views' | 'likes' | 'comments' | 'shares' | 'reach' | 'engagement'
  | 'linkClicks' | 'profileVisits' | 'followsGained' | 'watchTimeSeconds';
type StatCardConfig = {
  field: StatField;
  label: string;
  icon: React.ReactNode;
  iconBg: string;
  /** Custom formatter (watch time is a duration, not a count). */
  format?: (n: number) => string;
};

/**
 * Returns the stat cards to display for the given platform context.
 * When no specific platform is selected (showing all accounts) we fall back
 * to the generic 4-card view. Platform-specific selections relabel and/or
 * add cards to match what that network actually exposes (e.g. Facebook
 * surfaces Alcance, X talks about Impresiones, Bluesky has no views at all).
 */
function getStatCards(platform: Platform | null, kind: PostKind | null = null): StatCardConfig[] {
  const VIEWS: StatCardConfig = {
    field: 'views',
    label: 'Vistas',
    icon: <Eye size={18} />,
    iconBg: 'bg-blue-50 text-blue-600',
  };
  const PLAYS: StatCardConfig = {
    ...VIEWS,
    label: 'Reproducciones',
    icon: <PlayCircle size={18} />,
  };
  const IMPRESSIONS: StatCardConfig = { ...VIEWS, label: 'Impresiones' };
  const LIKES: StatCardConfig = {
    field: 'likes',
    label: 'Me gusta',
    icon: <Heart size={18} />,
    iconBg: 'bg-rose-50 text-rose-600',
  };
  const REACTIONS: StatCardConfig = { ...LIKES, label: 'Reacciones' };
  const COMMENTS: StatCardConfig = {
    field: 'comments',
    label: 'Comentarios',
    icon: <MessageCircle size={18} />,
    iconBg: 'bg-amber-50 text-amber-600',
  };
  const REPLIES: StatCardConfig = { ...COMMENTS, label: 'Respuestas' };
  const SHARES: StatCardConfig = {
    field: 'shares',
    label: 'Compartidos',
    icon: <Share2 size={18} />,
    iconBg: 'bg-emerald-50 text-emerald-600',
  };
  const REPOSTS: StatCardConfig = {
    ...SHARES,
    label: 'Reposts',
    icon: <Repeat2 size={18} />,
  };
  const SAVES: StatCardConfig = {
    ...SHARES,
    label: 'Guardados',
    icon: <Bookmark size={18} />,
  };
  const REACH: StatCardConfig = {
    field: 'reach',
    label: 'Alcance',
    icon: <Users size={18} />,
    iconBg: 'bg-violet-50 text-violet-600',
  };
  const CLICKS: StatCardConfig = {
    field: 'linkClicks',
    label: 'Clics en enlace',
    icon: <MousePointerClick size={18} />,
    iconBg: 'bg-cyan-50 text-cyan-600',
  };
  const PROFILE_VISITS: StatCardConfig = {
    field: 'profileVisits',
    label: 'Visitas al perfil',
    icon: <UserPlus size={18} />,
    iconBg: 'bg-indigo-50 text-indigo-600',
  };
  const FOLLOWS: StatCardConfig = {
    field: 'followsGained',
    label: 'Seguidores ganados',
    icon: <UserCheck size={18} />,
    iconBg: 'bg-teal-50 text-teal-600',
  };
  const SUBSCRIBERS: StatCardConfig = { ...FOLLOWS, label: 'Suscriptores ganados' };
  const WATCH_TIME: StatCardConfig = {
    field: 'watchTimeSeconds',
    label: 'Tiempo de visualización',
    icon: <Timer size={18} />,
    iconBg: 'bg-orange-50 text-orange-600',
    format: fmtDuration,
  };

  // IG kind overrides — Stories lean on Reach/Replies, Reels lean on Plays.
  if (platform === 'instagram' && kind === 'story') {
    return [REACH, LIKES, REPLIES, PROFILE_VISITS];
  }
  if (platform === 'instagram' && kind === 'reel') {
    return [REACH, PLAYS, LIKES, COMMENTS, SHARES, PROFILE_VISITS, FOLLOWS, WATCH_TIME];
  }
  switch (platform) {
    case 'instagram':
      return [REACH, VIEWS, LIKES, COMMENTS, SHARES, PROFILE_VISITS, FOLLOWS, WATCH_TIME];
    case 'facebook':
      return [REACH, PLAYS, LIKES, COMMENTS, SHARES];
    case 'twitter':
      return [IMPRESSIONS, LIKES, REPLIES, REPOSTS, CLICKS];
    case 'tiktok':
      return [PLAYS, LIKES, COMMENTS, SHARES];
    case 'tiktok_business':
      return [PLAYS, REACH, LIKES, COMMENTS, SHARES, FOLLOWS, WATCH_TIME, CLICKS];
    case 'threads':
      return [VIEWS, LIKES, REPLIES, REPOSTS];
    case 'linkedin':
      return [IMPRESSIONS, REACTIONS, COMMENTS, SHARES, CLICKS];
    case 'bluesky':
      return [LIKES, REPLIES, REPOSTS];
    case 'pinterest':
      return [IMPRESSIONS, REACTIONS, COMMENTS, SAVES, CLICKS];
    case 'youtube':
      return [PLAYS, LIKES, COMMENTS, SHARES, SUBSCRIBERS, WATCH_TIME];
    default:
      return [VIEWS, LIKES, COMMENTS, SHARES, FOLLOWS];
  }
}

/* ── Main component ─────────────────────────────────────── */
export function Analytics() {
  const qc = useQueryClient();
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [days, setDays] = useState<DayOption>(60);
  // Custom range: when set, the quick-range buttons are deselected and the
  // query uses ?from=...&to=... instead of ?days=N.
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  // Type filter: only meaningful for Instagram, where the feed mixes Posts,
  // Reels and Stories. null = no filter (everything).
  const [kindFilter, setKindFilter] = useState<PostKind | null>(null);
  // Vs-previous-period comparison. When on the stat cards render delta
  // arrows (▲ green / ▼ red) computed from the equivalent preceding window.
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [activeMetrics, setActiveMetrics] = useState<Set<MetricKey>>(
    new Set(['likes', 'comments', 'shares']),
  );
  const [chartMetrics, setChartMetrics] = useState<
    Array<{ key: MetricKey; label: string; color: string }>
  >(() => getChartMetrics(null));

  const { data: accountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ['social', 'accounts'],
    queryFn: () => api<{ accounts: Account[] }>('/api/social/accounts'),
  });

  const rangeKey = customRange ? `${customRange.from}_${customRange.to}` : `d${days}`;
  const queryKey = ['analytics', accountId ?? 'all', rangeKey, kindFilter ?? 'all', compareEnabled ? 'cmp' : 'nocmp'] as const;
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      if (customRange) {
        params.set('from', customRange.from);
        params.set('to', customRange.to);
      } else {
        params.set('days', String(days));
      }
      if (accountId) params.set('accountId', accountId);
      if (kindFilter) params.set('kind', kindFilter);
      if (compareEnabled) params.set('compare', 'previous');
      return api<AnalyticsData>(`/api/analytics?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  // When the server served stale cache and kicked a background refresh, refetch
  // a few seconds later to pull in the freshly-synced numbers — without a
  // blocking spinner on first paint. Capped so a slow/failing background sync
  // doesn't poll forever; resets whenever fresh data arrives.
  const swrAttempts = useRef(0);
  useEffect(() => {
    if (!data?.refreshing) { swrAttempts.current = 0; return; }
    if (swrAttempts.current >= 3) return;
    const t = setTimeout(() => { swrAttempts.current += 1; void refetch(); }, 7000);
    return () => clearTimeout(t);
  }, [data?.refreshing, refetch]);

  const syncMutation = useMutation({
    mutationFn: () => api<{ ok: true }>('/api/analytics/sync', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analytics'] });
    },
  });

  const accounts = (accountsData?.accounts ?? []).filter((a) => a.status === 'connected');
  const selectedAccount = accounts.find((a) => a.id === accountId);
  const totals = data?.totals;
  const series = data?.series ?? [];
  const topPosts = data?.topPosts ?? [];
  const postCount = data?.postCount ?? 0;

  const showEmpty = !isLoading && postCount === 0;

  // LinkedIn only exposes post analytics for Company Pages — personal profiles
  // have no analytics API, so a selected personal LinkedIn account will always
  // be empty. Explain it instead of showing a bare empty state.
  const linkedInPersonal =
    selectedAccount?.platform === 'linkedin' && selectedAccount.connectionType !== 'page';

  // Platform-specific stat cards: when a single account is selected we
  // show the metrics that platform actually exposes (and with the right
  // wording — Facebook talks about Alcance, X about Impresiones, etc.).
  // Instagram further branches on the Posts/Reels/Stories filter.
  const statCards = useMemo(
    () => getStatCards(selectedAccount?.platform ?? null, kindFilter),
    [selectedAccount?.platform, kindFilter],
  );

  // Mirror the same platform-aware logic on the chart: relabel chips,
  // hide metrics the platform doesn't expose, surface reach where useful.
  // Reset active metrics whenever the platform/kind changes so we don't keep
  // a toggle active that no longer exists in the current set.
  useEffect(() => {
    const metrics = getChartMetrics(selectedAccount?.platform ?? null, kindFilter);
    setChartMetrics(metrics);
    setActiveMetrics(new Set(metrics.slice(0, 3).map((m) => m.key)));
  }, [selectedAccount?.platform, kindFilter]);

  // The Posts/Reels/Stories filter only makes sense for Instagram. Clear it
  // when the user switches to any other platform (or to "all accounts") so
  // we don't carry a sticky filter into a context that can't satisfy it.
  useEffect(() => {
    if (selectedAccount?.platform !== 'instagram') setKindFilter(null);
  }, [selectedAccount?.platform]);

  function toggleMetric(k: MetricKey) {
    setActiveMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        if (next.size > 1) next.delete(k); // keep at least one active
      } else {
        next.add(k);
      }
      return next;
    });
  }

  function exportCsv() {
    if (series.length === 0) return;
    // Quote any value that contains a comma, quote, or newline (CSV escape rules).
    const esc = (v: string | number) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // Account / platform context — falls back to "Todas las cuentas" / "Todas"
    // when the filter is on every connected account.
    const accountLabel = selectedAccount
      ? selectedAccount.displayName ?? selectedAccount.username ?? PLATFORM_META[selectedAccount.platform].label
      : 'Todas las cuentas';
    const platformLabel = selectedAccount
      ? PLATFORM_META[selectedAccount.platform].label
      : 'Todas';
    const kindLabel = kindFilter
      ? ({ post: 'Posts', reel: 'Reels', story: 'Stories' } as const)[kindFilter]
      : 'Todo';
    const rows: string[] = [
      ['Cuenta', 'Red social', 'Tipo', 'Fecha', 'Me gusta', 'Comentarios', 'Compartidos', 'Vistas', 'Alcance'].join(','),
      ...series.map((s) =>
        [accountLabel, platformLabel, kindLabel, s.date, s.likes, s.comments, s.shares, s.views, s.reach].map(esc).join(','),
      ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = customRange
      ? `${customRange.from}_${customRange.to}`
      : `${days}d-${toYmd(new Date())}`;
    a.download = `analytics-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-7">
      {/* ── Header ── */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BarChart3 size={22} className="text-brand-600" /> Analítica</h1>
          <p className="mt-1 text-sm text-slate-500">
            Rendimiento de tus publicaciones en redes sociales.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={exportCsv}
            disabled={series.length === 0}
            className="btn-secondary gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Descargar datos en CSV"
          >
            <Download size={15} />
            <span className="hidden sm:inline">Exportar</span>
          </button>
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending || isFetching}
            className="btn-secondary gap-2"
            title="Refrescar métricas desde las redes sociales"
          >
            {syncMutation.isPending ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <RefreshCcw size={15} />
            )}
            <span className="hidden sm:inline">Actualizar</span>
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-4">
        {/* Account selector */}
        <div className="flex items-center gap-2 min-w-0">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">
            Cuenta
          </label>
          <AccountSelector
            accounts={accounts}
            value={accountId}
            onChange={setAccountId}
            loading={loadingAccounts}
            selectedAccount={selectedAccount}
          />
        </div>

        {/* Day range */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Periodo</span>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => {
                  setDays(d);
                  setCustomRange(null);
                }}
                className={cn(
                  'px-3 py-1 text-sm font-medium rounded-md transition',
                  !customRange && days === d
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700',
                )}
              >
                {d}d
              </button>
            ))}
          </div>
          <DateRangePicker
            value={customRange}
            onChange={setCustomRange}
            maxDays={MAX_CUSTOM_DAYS}
          />
        </div>

        {/* Comparison toggle — when on the StatCards render delta arrows vs
            the equivalent preceding window. Kept compact: a single pill
            with a check; arrows do the actual heavy lifting on the cards. */}
        <button
          type="button"
          onClick={() => setCompareEnabled((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1.5 text-xs font-medium rounded-md px-2.5 py-1.5 border transition',
            compareEnabled
              ? 'bg-brand-50 text-brand-700 border-brand-200'
              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-800',
          )}
          title="Comparar con el periodo anterior equivalente"
        >
          <TrendingUp size={12} />
          Comparar con periodo anterior
        </button>

        {(accountId || customRange || kindFilter || compareEnabled) && (
          <button
            onClick={() => {
              setAccountId(undefined);
              setCustomRange(null);
              setDays(60);
              setKindFilter(null);
              setCompareEnabled(false);
            }}
            className="text-xs text-brand-600 hover:text-brand-700 font-medium"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Posts / Reels / Stories selector — only meaningful for Instagram.
          We surface it once an IG account is selected so the user can drill
          into a single content type and see its specific metrics. */}
      {selectedAccount?.platform === 'instagram' && data?.kindCounts && (
        <KindSelector
          value={kindFilter}
          onChange={setKindFilter}
          counts={data.kindCounts}
        />
      )}

      {/* ── Loading state ── */}
      {isLoading && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 flex flex-col items-center gap-3 text-slate-400">
          <Loader2 size={28} className="animate-spin text-slate-300" />
          <p className="text-sm">Cargando métricas…</p>
        </div>
      )}

      {/* ── LinkedIn personal-profile notice ── */}
      {linkedInPersonal && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3">
          <Info size={18} className="text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">LinkedIn no reporta analíticas de perfiles personales</p>
            <p className="mt-1 text-blue-800/90">
              LinkedIn solo ofrece métricas de publicaciones para <strong>páginas de empresa</strong>, no para
              perfiles personales. Conecta y publica en una página de empresa para ver sus analíticas aquí.
            </p>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {showEmpty && !linkedInPersonal && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 flex flex-col items-center gap-3 text-center px-6">
          <BarChart3 size={36} className="text-slate-300" strokeWidth={1.5} />
          {data?.outsideRange ? (
            // The user has metrics — just not in the window they picked.
            // Tell them when the last activity was and offer a one-click jump
            // to the 90d range (the widest preset we support).
            <>
              <p className="text-base font-semibold text-slate-700">
                {selectedAccount ? 'Esta cuenta no tiene actividad en este periodo' : 'No hay actividad en este periodo'}
              </p>
              <p className="text-sm text-slate-500 max-w-md">
                {selectedAccount
                  ? `Tenemos ${data.outsideRange.totalCount} publicaciones registradas para esta cuenta. La más reciente es del ${fmtDateLong(data.outsideRange.latestPostedAt)}.`
                  : `Tenemos ${data.outsideRange.totalCount} publicaciones registradas. La más reciente es del ${fmtDateLong(data.outsideRange.latestPostedAt)}.`}
              </p>
              {!customRange && days < 90 && (
                <button
                  type="button"
                  onClick={() => setDays(90)}
                  className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-md px-3 py-1.5 transition"
                >
                  Ampliar a 90 días
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-base font-semibold text-slate-700">Aún no tienes datos de analítica</p>
              <p className="text-sm text-slate-500 max-w-md">
                Conecta tus cuentas y publica para empezar a ver métricas. Las redes sociales tardan
                unos minutos en reportar los primeros datos tras una publicación.
              </p>
            </>
          )}
        </div>
      )}

      {/* ── Stats ── */}
      {!isLoading && !showEmpty && totals && (
        <>
          <div
            className={cn(
              'grid gap-4 grid-cols-2',
              statCards.length === 3 && 'lg:grid-cols-3',
              statCards.length === 4 && 'lg:grid-cols-4',
              statCards.length === 5 && 'lg:grid-cols-5',
              // 6 cards → two balanced rows of three rather than six skinny ones.
              statCards.length === 6 && 'lg:grid-cols-3',
              statCards.length >= 7 && 'lg:grid-cols-4',
            )}
          >
            {statCards.map((c) => {
              const value = totals[c.field] ?? 0;
              const prev = data?.compare?.totals?.[c.field];
              const format = c.format ?? fmtNum;
              return (
                <StatCard
                  key={c.field + c.label}
                  icon={c.icon}
                  iconBg={c.iconBg}
                  label={c.label}
                  value={format(value)}
                  fullValue={c.format ? c.format(value) : fmtFull(value)}
                  sub={`${postCount > 0 ? format(Math.round(value / postCount)) : format(0)} por publicación`}
                  delta={compareEnabled ? computeDelta(value, prev) : null}
                />
              );
            })}
          </div>
          {/* Engagement summary card — likes + comments + shares, the
              single "did people care" number. Lives below the per-metric
              grid so the breakdown reads first and the total anchors it. */}
          <EngagementCard
            value={totals.engagement}
            postCount={postCount}
            delta={
              compareEnabled
                ? computeDelta(totals.engagement, data?.compare?.totals.engagement)
                : null
            }
          />
          {compareEnabled && data?.compare && (
            <p className="-mt-2 text-xs text-slate-500">
              Comparando con {fmtDateLong(data.compare.from)} – {fmtDateLong(data.compare.to)}
            </p>
          )}

          {/* ── Daily engagement series ── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-700">Evolución diaria</h2>
                <span className="text-xs text-slate-400">· {postCount} publicaciones</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {chartMetrics.map((m) => {
                  const active = activeMetrics.has(m.key);
                  return (
                    <button
                      key={m.key}
                      onClick={() => toggleMetric(m.key)}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition border',
                        active
                          ? 'bg-slate-50 text-slate-700 border-slate-200'
                          : 'bg-white text-slate-400 border-slate-100 hover:text-slate-600',
                      )}
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: active ? m.color : '#cbd5e1' }}
                      />
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <LineChart series={series} activeMetrics={activeMetrics} chartMetrics={chartMetrics} />
          </div>

          {/* ── Best times (last 12 months, independent of the range above) ── */}
          <BestTimesCard accountId={accountId} />

          {/* ── Top posts ── */}
          {topPosts.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100">
                <h2 className="text-sm font-semibold text-slate-700">Mejores publicaciones</h2>
                <p className="text-xs text-slate-400 mt-0.5">Ordenadas por engagement total</p>
              </div>
              <ul className="divide-y divide-slate-50">
                {topPosts.map((p) => (
                  <TopPostRow key={p.id} post={p} />
                ))}
              </ul>
            </div>
          )}
        </>
      )}

    </div>
  );
}

/* ── Account selector dropdown ──────────────────────────── */
function AccountSelector({
  accounts,
  value,
  onChange,
  loading,
  selectedAccount,
}: {
  accounts: Account[];
  value: string | undefined;
  onChange: (id: string | undefined) => void;
  loading: boolean;
  selectedAccount: Account | undefined;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onEsc);
    }
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={loading}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white pl-2 pr-2.5 py-1.5 text-sm text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400 min-w-[180px] max-w-[260px]"
      >
        {selectedAccount ? (
          <>
            <AccountAvatar account={selectedAccount} size={20} />
            <span className="truncate">
              {selectedAccount.displayName ?? selectedAccount.username ?? PLATFORM_META[selectedAccount.platform].label}
            </span>
          </>
        ) : (
          <span className="text-slate-500 flex-1 text-left">Todas las cuentas</span>
        )}
        <ChevronDown size={14} className="text-slate-400 ml-auto shrink-0" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 left-0 right-auto min-w-[260px] max-h-[320px] overflow-auto bg-white border border-slate-200 rounded-xl shadow-lg p-1">
          <button
            type="button"
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
            className={cn(
              'w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm hover:bg-slate-50',
              !value && 'bg-brand-50/50',
            )}
          >
            <span className="h-5 w-5 rounded-full bg-slate-100 inline-flex items-center justify-center shrink-0">
              <BarChart3 size={12} className="text-slate-500" />
            </span>
            <span className="flex-1 text-left">Todas las cuentas</span>
            {!value && <Check size={14} className="text-brand-600" />}
          </button>
          {accounts.length > 0 && <div className="h-px bg-slate-100 my-1" />}
          {accounts.map((a) => {
            const meta = PLATFORM_META[a.platform];
            const label = a.displayName ?? a.username ?? meta.label;
            const isSelected = value === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => {
                  onChange(a.id);
                  setOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm hover:bg-slate-50',
                  isSelected && 'bg-brand-50/50',
                )}
              >
                <AccountAvatar account={a} size={20} />
                <span className="flex-1 text-left min-w-0">
                  <span className="block truncate text-slate-700">{label}</span>
                  <span className="block text-[10px] text-slate-400 leading-tight">{meta.label}</span>
                </span>
                {isSelected && <Check size={14} className="text-brand-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── SVG line chart ─────────────────────────────────────── */
function LineChart({
  series,
  activeMetrics,
  chartMetrics,
}: {
  series: AnalyticsData['series'];
  activeMetrics: Set<MetricKey>;
  chartMetrics: Array<{ key: MetricKey; label: string; color: string }>;
}) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const height = 220;
  const paddingL = 38;
  const paddingR = 12;
  const paddingT = 16;
  const paddingB = 28;

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setWidth(Math.max(280, Math.round(e.contentRect.width)));
      }
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const chartW = width - paddingL - paddingR;
  const chartH = height - paddingT - paddingB;

  const max = useMemo(() => {
    let m = 0;
    for (const s of series) {
      for (const k of activeMetrics) {
        const v = s[k];
        if (v > m) m = v;
      }
    }
    return m;
  }, [series, activeMetrics]);

  const niceMax = useMemo(() => niceCeil(max), [max]);

  if (series.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-slate-400">
        Sin actividad en este periodo.
      </div>
    );
  }

  const n = series.length;
  const stepX = n > 1 ? chartW / (n - 1) : 0;

  function xFor(i: number) {
    return paddingL + i * stepX;
  }
  function yFor(v: number) {
    if (niceMax === 0) return paddingT + chartH;
    return paddingT + chartH - (v / niceMax) * chartH;
  }

  // Y-axis ticks: 0, 25%, 50%, 75%, 100%
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => ({
    y: paddingT + chartH - p * chartH,
    value: Math.round(niceMax * p),
  }));

  // X-axis labels: show ~5 evenly spaced
  const labelEvery = Math.max(1, Math.ceil(n / 6));

  function onMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const x = e.clientX - rect.left - paddingL;
    if (stepX === 0) {
      setHoverIdx(0);
      return;
    }
    const idx = Math.round(x / stepX);
    if (idx >= 0 && idx < n) setHoverIdx(idx);
    else setHoverIdx(null);
  }

  return (
    <div ref={wrapRef} className="relative">
      <svg
        width={width}
        height={height}
        className="block select-none"
        onMouseMove={onMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* gridlines + y labels */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={paddingL}
              x2={paddingL + chartW}
              y1={t.y}
              y2={t.y}
              stroke="#f1f5f9"
              strokeWidth={1}
            />
            <text
              x={paddingL - 6}
              y={t.y + 3}
              textAnchor="end"
              className="fill-slate-400"
              style={{ fontSize: 10 }}
            >
              {fmtNum(t.value)}
            </text>
          </g>
        ))}

        {/* x labels */}
        {series.map((s, i) => {
          if (i % labelEvery !== 0 && i !== n - 1) return null;
          return (
            <text
              key={s.date}
              x={xFor(i)}
              y={height - 8}
              textAnchor="middle"
              className="fill-slate-400"
              style={{ fontSize: 10 }}
            >
              {fmtDateShort(s.date)}
            </text>
          );
        })}

        {/* metric lines (with area gradient for single-metric mode) */}
        {chartMetrics.map((m) => {
          if (!activeMetrics.has(m.key)) return null;
          const points = series.map((s, i) => `${xFor(i)},${yFor(s[m.key])}`).join(' ');
          const areaPath =
            activeMetrics.size === 1
              ? `M ${xFor(0)},${paddingT + chartH} L ${series
                  .map((s, i) => `${xFor(i)},${yFor(s[m.key])}`)
                  .join(' L ')} L ${xFor(n - 1)},${paddingT + chartH} Z`
              : null;
          return (
            <g key={m.key}>
              {areaPath && (
                <path d={areaPath} fill={m.color} opacity={0.08} />
              )}
              <polyline
                points={points}
                fill="none"
                stroke={m.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </g>
          );
        })}

        {/* hover guide + dots */}
        {hoverIdx !== null && (
          <g>
            <line
              x1={xFor(hoverIdx)}
              x2={xFor(hoverIdx)}
              y1={paddingT}
              y2={paddingT + chartH}
              stroke="#cbd5e1"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            {chartMetrics.map((m) => {
              if (!activeMetrics.has(m.key)) return null;
              const s = series[hoverIdx]!;
              return (
                <circle
                  key={m.key}
                  cx={xFor(hoverIdx)}
                  cy={yFor(s[m.key])}
                  r={3.5}
                  fill="white"
                  stroke={m.color}
                  strokeWidth={2}
                />
              );
            })}
          </g>
        )}
      </svg>

      {/* tooltip */}
      {hoverIdx !== null && (
        <ChartTooltip
          point={series[hoverIdx]!}
          activeMetrics={activeMetrics}
          chartMetrics={chartMetrics}
          x={xFor(hoverIdx)}
          chartWidth={width}
        />
      )}
    </div>
  );
}

function ChartTooltip({
  point,
  activeMetrics,
  chartMetrics,
  x,
  chartWidth,
}: {
  point: AnalyticsData['series'][number];
  activeMetrics: Set<MetricKey>;
  chartMetrics: Array<{ key: MetricKey; label: string; color: string }>;
  x: number;
  chartWidth: number;
}) {
  // Position tooltip: above the chart, flipped to the left if near right edge
  const flipLeft = x > chartWidth - 140;
  return (
    <div
      className="pointer-events-none absolute z-10 rounded-lg bg-slate-900 text-white px-3 py-2 shadow-lg text-xs"
      style={{
        left: flipLeft ? x - 8 : x + 8,
        top: 4,
        transform: flipLeft ? 'translateX(-100%)' : undefined,
        minWidth: 130,
      }}
    >
      <div className="font-semibold mb-1">{fmtDateLong(point.date)}</div>
      <ul className="space-y-0.5">
        {chartMetrics.map((m) => {
          if (!activeMetrics.has(m.key)) return null;
          return (
            <li key={m.key} className="flex items-center gap-2">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: m.color }}
              />
              <span className="flex-1 text-slate-300">{m.label}</span>
              <span className="font-semibold tabular-nums">{fmtFull(point[m.key])}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Round up to a "nice" axis max so gridlines look clean (e.g. 87 → 100, 4300 → 5000). */
function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const exp = Math.floor(Math.log10(n));
  const base = Math.pow(10, exp);
  const norm = n / base;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * base;
}

/* ── Stat card ──────────────────────────────────────────── */
type CardDelta =
  | { kind: 'up' | 'down'; pct: number; previousValue: number }
  // The previous window had zero on this metric but we did grow → "Nuevo"
  // is more readable than "+∞ %".
  | { kind: 'new'; previousValue: number }
  | { kind: 'flat'; previousValue: number };

/** Compare two numeric metric values into the shape StatCard expects. */
function computeDelta(current: number, previous: number | undefined | null): CardDelta | null {
  if (previous == null) return null;
  if (previous === 0 && current === 0) return { kind: 'flat', previousValue: 0 };
  if (previous === 0) return { kind: 'new', previousValue: 0 };
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(pct) < 0.5) return { kind: 'flat', previousValue: previous };
  return { kind: pct > 0 ? 'up' : 'down', pct, previousValue: previous };
}

function StatCard({
  icon,
  iconBg,
  label,
  value,
  fullValue,
  sub,
  delta,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: string;
  fullValue: string;
  sub: string;
  delta?: CardDelta | null;
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className={cn('h-9 w-9 rounded-xl flex items-center justify-center', iconBg)}>
          {icon}
        </div>
        {delta && <DeltaPill delta={delta} />}
      </div>
      <div>
        <p className="text-2xl font-extrabold text-slate-900" title={fullValue}>{value}</p>
        <p className="text-xs font-semibold text-slate-600 mt-0.5">{label}</p>
        <p className="text-xs text-slate-400">{sub}</p>
      </div>
    </div>
  );
}

/* ── Engagement summary card (full-width) ───────────────── */
function EngagementCard({
  value,
  postCount,
  delta,
}: {
  value: number;
  postCount: number;
  delta?: CardDelta | null;
}) {
  const perPost = postCount > 0 ? Math.round(value / postCount) : 0;
  return (
    <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl border border-brand-100 shadow-sm p-5 flex items-center gap-4">
      <div className="h-12 w-12 rounded-xl bg-brand-100 text-brand-600 flex items-center justify-center shrink-0">
        <Sparkles size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-3xl font-extrabold text-slate-900 tabular-nums" title={fmt.format(value)}>
            {fmtNum(value)}
          </p>
          {delta && <DeltaPill delta={delta} />}
        </div>
        <p className="text-sm font-semibold text-slate-700 mt-0.5">Engagement total</p>
        <p className="text-xs text-slate-500">
          Me gusta + comentarios + compartidos · {fmtNum(perPost)} por publicación
        </p>
      </div>
    </div>
  );
}

function DeltaPill({ delta }: { delta: CardDelta }) {
  const tip = `Periodo anterior: ${fmt.format(delta.previousValue)}`;
  if (delta.kind === 'flat') {
    return (
      <span
        title={tip}
        className="inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full px-1.5 py-0.5 bg-slate-100 text-slate-500"
      >
        <Minus size={10} />
        0%
      </span>
    );
  }
  if (delta.kind === 'new') {
    return (
      <span
        title={tip}
        className="inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full px-1.5 py-0.5 bg-emerald-50 text-emerald-700"
      >
        <ArrowUpRight size={10} />
        Nuevo
      </span>
    );
  }
  const isUp = delta.kind === 'up';
  const abs = Math.abs(delta.pct);
  // Show one decimal under 10%, integer above — keeps the pill compact.
  const pctLabel = abs >= 100
    ? `${Math.round(abs)}%`
    : abs >= 10
      ? `${Math.round(abs)}%`
      : `${abs.toFixed(1)}%`;
  return (
    <span
      title={tip}
      className={cn(
        'inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full px-1.5 py-0.5 tabular-nums',
        isUp ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700',
      )}
    >
      {isUp ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
      {isUp ? '+' : '−'}
      {pctLabel}
    </span>
  );
}

/* ── Custom date range picker ───────────────────────────── */
function DateRangePicker({
  value,
  onChange,
  maxDays,
}: {
  value: { from: string; to: string } | null;
  onChange: (range: { from: string; to: string } | null) => void;
  maxDays: number;
}) {
  const [open, setOpen] = useState(false);
  // Working copies — only applied on "Aplicar" so the chart doesn't refetch
  // on every keystroke as the user types a date.
  const [from, setFrom] = useState(value?.from ?? '');
  const [to, setTo] = useState(value?.to ?? '');
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Sync working copies when the parent value changes externally
  // (e.g. user clicked a quick-range button or "Limpiar filtros").
  useEffect(() => {
    setFrom(value?.from ?? '');
    setTo(value?.to ?? '');
  }, [value?.from, value?.to]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onEsc);
    }
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const todayYmd = toYmd(new Date());

  function apply() {
    setError(null);
    if (!from || !to) {
      setError('Selecciona ambas fechas.');
      return;
    }
    if (from > to) {
      setError('La fecha inicial debe ser anterior a la final.');
      return;
    }
    const span =
      Math.floor((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000) + 1;
    if (span > maxDays) {
      setError(`Máximo ${maxDays} días en un rango.`);
      return;
    }
    if (to > todayYmd) {
      setError('La fecha final no puede ser futura.');
      return;
    }
    onChange({ from, to });
    setOpen(false);
  }

  const label = value
    ? `${fmtDateShortLocal(value.from)} – ${fmtDateShortLocal(value.to)}`
    : 'Personalizado';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border text-sm font-medium transition',
          value
            ? 'border-brand-200 bg-brand-50 text-brand-700'
            : 'border-slate-200 bg-white text-slate-600 hover:text-slate-800 hover:border-slate-300',
        )}
        title={`Rango personalizado (máx. ${maxDays} días)`}
      >
        <CalendarRange size={14} />
        <span>{label}</span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }
            }}
            className="ml-1 inline-flex items-center justify-center rounded hover:bg-brand-100 p-0.5 cursor-pointer"
            title="Quitar rango personalizado"
          >
            <X size={12} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-30 mt-1 right-0 sm:left-0 sm:right-auto bg-white border border-slate-200 rounded-xl shadow-lg p-4 w-[300px]">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Rango personalizado
          </p>
          <div className="flex flex-col gap-2.5">
            <label className="text-xs text-slate-600">
              Desde
              <input
                type="date"
                value={from}
                max={to || todayYmd}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
              />
            </label>
            <label className="text-xs text-slate-600">
              Hasta
              <input
                type="date"
                value={to}
                min={from || undefined}
                max={todayYmd}
                onChange={(e) => setTo(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
              />
            </label>
          </div>
          {error && (
            <p className="mt-2 text-xs text-rose-600">{error}</p>
          )}
          <p className="mt-3 text-[11px] text-slate-400">Máximo {maxDays} días por rango.</p>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs px-3 py-1.5 text-slate-500 hover:text-slate-700 font-medium"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={apply}
              className="text-xs px-3 py-1.5 rounded-lg bg-brand-600 text-white font-semibold hover:bg-brand-700"
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact label like "5 may" used inside the date-range chip. */
function fmtDateShortLocal(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

/* ── Posts / Reels / Stories selector (Instagram only) ──── */
function KindSelector({
  value,
  onChange,
  counts,
}: {
  value: PostKind | null;
  onChange: (v: PostKind | null) => void;
  counts: { post: number; reel: number; story: number };
}) {
  const tabs: Array<{ key: PostKind | null; label: string; count: number }> = [
    { key: null, label: 'Todo', count: counts.post + counts.reel + counts.story },
    { key: 'post', label: 'Posts', count: counts.post },
    { key: 'reel', label: 'Reels', count: counts.reel },
    { key: 'story', label: 'Stories', count: counts.story },
  ];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 flex items-center gap-3 flex-wrap">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo</span>
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
        {tabs.map((t) => {
          const active = value === t.key;
          return (
            <button
              key={t.key ?? 'all'}
              onClick={() => onChange(t.key)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-md transition',
                active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {t.label}
              <span
                className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded-full tabular-nums',
                  active ? 'bg-brand-50 text-brand-700' : 'bg-slate-200 text-slate-600',
                )}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>
      {value === 'story' && (
        <span className="text-[11px] text-slate-400 italic">
          Las stories caducan a las 24h en Instagram — capturamos su estado mientras siguen vivas.
        </span>
      )}
    </div>
  );
}

/* ── Top post row ───────────────────────────────────────── */
type TopPost = AnalyticsData['topPosts'][number];

function TopPostRow({ post: p }: { post: TopPost }) {
  const platformKey = isPlatform(p.platform) ? p.platform : null;
  const meta = platformKey ? PLATFORM_META[platformKey] : null;
  const Icon = meta?.icon;

  // Pick the platform's metric labels (Alcance / Reproducciones / etc.) so the
  // bottom row reads natural for that network — same logic as the stat cards.
  // For Instagram, also branch on the post's kind so Reels show Reproducciones
  // and Stories show Reach/Respuestas instead of Vistas/Comentarios.
  const cards = getStatCards(platformKey, p.kind);
  const values: Record<StatField, number> = {
    reach: p.reach,
    views: p.views,
    likes: p.likes,
    comments: p.comments,
    shares: p.shares,
    linkClicks: p.linkClicks,
    profileVisits: p.profileVisits,
    followsGained: p.followsGained ?? 0,
    watchTimeSeconds: p.watchTimeSeconds ?? 0,
    // getStatCards never emits an 'engagement' card for a row, but the map
    // must satisfy Record<StatField, number>; derive it for completeness.
    engagement: p.likes + p.comments + p.shares,
  };

  return (
    <li className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 px-4 sm:px-5 py-4 hover:bg-slate-50 transition">
      {/* Thumbnail + content sit side-by-side; the "Ver post" button drops
          below them on mobile (sm:flex-row lifts it back to the right). */}
      <div className="flex gap-3 sm:gap-4 flex-1 min-w-0">
      <PostThumbnail mediaUrls={p.mediaUrls} platform={platformKey} />

      <div className="flex-1 min-w-0">
        {/* Header: platform icon · "Instagram" · **Account name** · 29 abr 2026 */}
        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
          {Icon && meta && <Icon size={12} color={meta.color} />}
          <span className="text-[11px] text-slate-400">{meta?.label ?? p.platform}</span>
          {p.kind !== 'post' && (
            <span
              className={cn(
                'text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wider',
                p.kind === 'story' && 'bg-pink-50 text-pink-700',
                p.kind === 'reel' && 'bg-violet-50 text-violet-700',
              )}
            >
              {p.kind === 'story' ? 'Story' : 'Reel'}
            </span>
          )}
          {p.accountName && (
            <>
              <span className="text-[11px] text-slate-300">·</span>
              <span className="text-[11px] font-semibold text-slate-600 truncate max-w-[200px]">
                {p.accountName}
              </span>
            </>
          )}
          {p.postedAt && (
            <>
              <span className="text-[11px] text-slate-300">·</span>
              <span className="text-[11px] text-slate-400">{fmtDateLong(p.postedAt)}</span>
            </>
          )}
        </div>

        <p className="text-sm text-slate-700 leading-snug">
          {p.caption ? truncate(p.caption, 120) : <em className="text-slate-400">Sin descripción</em>}
        </p>

        {/* Metrics row: icon + label + value, platform-aware labels.
           Hide metrics that the platform doesn't expose (e.g. Bluesky has
           no views). Always show likes/comments/shares; only show reach /
           views when the platform actually surfaces them. */}
        <div className="mt-2.5 flex items-center gap-x-4 gap-y-1.5 text-xs text-slate-500 flex-wrap">
          {cards.map((c) => {
            const v = values[c.field];
            return (
              <span key={c.field + c.label} className="inline-flex items-center gap-1">
                <span className={cn('inline-flex h-5 w-5 rounded-md items-center justify-center', c.iconBg)}>
                  {c.icon && (
                    <span className="[&>svg]:!h-3 [&>svg]:!w-3">{c.icon}</span>
                  )}
                </span>
                <span className="text-slate-500">{c.label}</span>
                <span className="font-semibold text-slate-700 tabular-nums">{(c.format ?? fmtNum)(v)}</span>
              </span>
            );
          })}
        </div>
      </div>
      </div>

      {p.platformUrl && (
        <a
          href={p.platformUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="shrink-0 w-full justify-center sm:w-auto sm:justify-start sm:self-start inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:bg-slate-50 hover:text-brand-700 hover:border-brand-200 transition"
        >
          <ExternalLink size={13} />
          <span>
            Ver{' '}
            {p.kind === 'reel' ? 'reel' : p.kind === 'story' ? 'story' : 'post'}
            {meta && (
              <>
                {' '}en <span className="font-semibold">{meta.label}</span>
              </>
            )}
          </span>
        </a>
      )}
    </li>
  );
}

/**
 * Renders the post thumbnail. Supports three cases:
 *   1. No media → fall back to the platform icon.
 *   2. Single image/video → renders it (videos use a muted <video> with
 *      preload="metadata" so the first frame paints as a poster).
 *   3. Multiple slides → mini-carousel with prev/next chevrons and a
 *      "1/N" badge so the user can flip through every media file of a
 *      multi-image Instagram/Facebook post.
 *
 * Any load failure on a specific slide marks just that slide as errored —
 * the other slides keep working. If every slide errors we fall back to
 * the platform icon to avoid a broken-image glyph.
 */
function PostThumbnail({ mediaUrls, platform }: { mediaUrls: string[]; platform: Platform | null }) {
  const [idx, setIdx] = useState(0);
  const [errored, setErrored] = useState<Set<number>>(new Set());
  const meta = platform ? PLATFORM_META[platform] : null;
  const Icon = meta?.icon;

  const total = mediaUrls.length;
  const safeIdx = total > 0 ? idx % total : 0;
  const currentUrl = mediaUrls[safeIdx] ?? null;
  const isCurrentErrored = errored.has(safeIdx);
  const isVideo = currentUrl != null && /\.(mp4|mov|webm|m4v|avi)(\?|$)/i.test(currentUrl);
  const showMedia = currentUrl != null && !isCurrentErrored;
  const allErrored = total > 0 && errored.size >= total;

  function go(delta: number, e?: React.MouseEvent | React.KeyboardEvent) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (total === 0) return;
    setIdx((i) => (i + delta + total) % total);
  }

  return (
    <div className="shrink-0 h-24 w-24 sm:h-32 sm:w-32 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center relative group">
      {showMedia && !allErrored ? (
        isVideo ? (
          <video
            key={currentUrl}
            src={currentUrl}
            muted
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
            onError={() => setErrored((p) => new Set(p).add(safeIdx))}
          />
        ) : (
          <img
            key={currentUrl}
            src={currentUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
            onError={() => setErrored((p) => new Set(p).add(safeIdx))}
          />
        )
      ) : Icon && meta ? (
        <Icon size={40} color={meta.color} />
      ) : null}

      {/* Video play badge */}
      {showMedia && isVideo && (
        <span className="absolute bottom-1.5 right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white pointer-events-none">
          <PlayCircle size={14} />
        </span>
      )}

      {/* Multi-slide indicator + carousel controls */}
      {total > 1 && (
        <>
          {/* Slide counter badge — always visible top-right */}
          <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/55 text-white text-[10px] font-semibold tabular-nums pointer-events-none">
            <Images size={10} />
            {safeIdx + 1}/{total}
          </span>

          {/* Prev / next chevrons — fade in on hover */}
          <button
            type="button"
            onClick={(e) => go(-1, e)}
            aria-label="Slide anterior"
            className="absolute left-1 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition focus:opacity-100 focus:outline-none"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => go(1, e)}
            aria-label="Slide siguiente"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition focus:opacity-100 focus:outline-none"
          >
            <ChevronRight size={14} />
          </button>

          {/* Dot pager (only shown when there are 2–5 slides — saves space
              when the carousel is huge, e.g. an IG album with 10 photos). */}
          {total <= 5 && (
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 pointer-events-none">
              {Array.from({ length: total }).map((_, i) => (
                <span
                  key={i}
                  className={cn(
                    'h-1 w-1 rounded-full transition-colors',
                    i === safeIdx ? 'bg-white' : 'bg-white/45',
                  )}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
